import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faSync,
  faTimes,
  faExclamationCircle,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { schwabApi } from '../api/schwabApi';
import { useAuth } from '../contexts/AuthContext';

const ORDER_STATUSES = [
  '',
  'WORKING',
  'QUEUED',
  'PENDING_ACTIVATION',
  'ACCEPTED',
  'FILLED',
  'CANCELED',
  'REJECTED',
  'EXPIRED',
  'PENDING_CANCEL',
  'PENDING_REPLACE',
  'REPLACED',
];

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusTone(status) {
  const s = String(status || '').toUpperCase();
  if (['FILLED'].includes(s)) return 'text-bull';
  if (['REJECTED', 'CANCELED', 'EXPIRED'].includes(s)) return 'text-bear';
  if (['WORKING', 'QUEUED', 'PENDING_ACTIVATION', 'ACCEPTED', 'NEW'].includes(s)) return 'text-action';
  return 'text-muted';
}

function maskAccount(num) {
  if (!num) return '—';
  const s = String(num);
  return s.length <= 4 ? s : `••••${s.slice(-4)}`;
}

/**
 * @param {{
 *  accounts: Array,
 *  viewMode: 'all'|'single',
 *  selectedAccount: string,
 *  accountLabel: (acct) => string,
 *  ticketDraft?: object|null,
 *  onTicketDraftConsumed?: () => void,
 * }} props
 */
export default function SchwabOrdersPanel({
  accounts,
  viewMode,
  selectedAccount,
  accountLabel,
  ticketDraft = null,
  onTicketDraftConsumed,
}) {
  const { maskAmounts } = useAuth();
  const money = (v) => (maskAmounts ? '••••' : formatCurrency(v));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState('');
  const [days, setDays] = useState('7');
  const [cancelingId, setCancelingId] = useState(null);

  // Place order form
  const [showForm, setShowForm] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    accountNumber: '',
    symbol: '',
    instruction: 'BUY',
    quantity: '',
    orderType: 'MARKET',
    price: '',
    stopPrice: '',
    duration: 'DAY',
    session: 'NORMAL',
    assetType: 'EQUITY',
  });

  const appliedDraftId = React.useRef(null);

  useEffect(() => {
    if (!ticketDraft?.id || appliedDraftId.current === ticketDraft.id) return;
    appliedDraftId.current = ticketDraft.id;
    const { id: _draftId, ...fields } = ticketDraft;
    setForm((f) => ({
      ...f,
      ...fields,
      accountNumber: fields.accountNumber || f.accountNumber,
    }));
    setShowForm(true);
    setMessage(null);
    setError(null);
    onTicketDraftConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDraft]);

  useEffect(() => {
    if (viewMode === 'single' && selectedAccount) {
      setForm((f) =>
        f.accountNumber === selectedAccount ? f : { ...f, accountNumber: selectedAccount }
      );
    } else if (accounts[0]) {
      setForm((f) =>
        f.accountNumber ? f : { ...f, accountNumber: accounts[0].accountNumber }
      );
    }
  }, [viewMode, selectedAccount, accounts]);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        days,
        status: status || undefined,
        accountNumber:
          viewMode === 'single' && selectedAccount ? selectedAccount : undefined,
      };
      const res = await schwabApi.getOrders(params);
      setOrders(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [days, status, viewMode, selectedAccount]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const openWorking = useMemo(
    () =>
      orders.filter((o) =>
        ['WORKING', 'QUEUED', 'PENDING_ACTIVATION', 'ACCEPTED', 'AWAITING_CONDITION', 'NEW'].includes(
          String(o.status || '').toUpperCase()
        )
      ).length,
    [orders]
  );

  const handleCancel = async (order) => {
    if (!order.cancelable) return;
    if (!window.confirm(`Cancel order #${order.orderId} (${order.symbol || 'multi-leg'})?`)) {
      return;
    }
    try {
      setCancelingId(order.orderId);
      setMessage(null);
      await schwabApi.cancelOrder(order.orderId, order.accountNumber);
      setMessage({ tone: 'bull', text: `Canceled order #${order.orderId}` });
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCancelingId(null);
    }
  };

  const handlePlace = async (e) => {
    e.preventDefault();
    try {
      setPlacing(true);
      setError(null);
      setMessage(null);
      const payload = {
        accountNumber: form.accountNumber,
        symbol: form.symbol,
        instruction: form.instruction,
        quantity: Number(form.quantity),
        orderType: form.orderType,
        duration: form.duration,
        session: form.session,
        assetType: form.assetType,
      };
      if (form.price !== '') payload.price = form.price;
      if (form.stopPrice !== '') payload.stopPrice = form.stopPrice;

      const res = await schwabApi.placeOrder(payload);
      setMessage({
        tone: 'bull',
        text: res.data?.orderId
          ? `Order placed (#${res.data.orderId})`
          : 'Order placed successfully',
      });
      setShowForm(false);
      setForm((f) => ({ ...f, symbol: '', quantity: '', price: '', stopPrice: '' }));
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink flex-1 sm:flex-none"
          >
            <option value="1">Last 1 day</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink flex-1 sm:flex-none"
          >
            <option value="">All statuses</option>
            {ORDER_STATUSES.filter(Boolean).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted num w-full sm:w-auto">
            {orders.length} orders · {openWorking} working
          </span>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="flex-1 sm:flex-none text-xs px-3 py-1.5 rounded-md bg-action text-white hover:opacity-90"
          >
            {showForm ? 'Close ticket' : 'Place order'}
          </button>
          <button
            type="button"
            onClick={loadOrders}
            disabled={loading}
            className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-hairline text-muted hover:text-ink"
          >
            <FontAwesomeIcon icon={faSync} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`flex items-start gap-2 px-3 py-2 rounded-md border border-hairline text-sm ${
            message.tone === 'bull' ? 'text-bull bg-elevated' : 'text-muted bg-elevated'
          }`}
        >
          <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5" />
          <span>{message.text}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-hairline text-sm text-bear bg-elevated">
          <FontAwesomeIcon icon={faExclamationCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {showForm && (
        <form onSubmit={handlePlace} className="lt-card px-4 py-3 space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-sm font-medium text-ink">Quick ticket</div>
            <p className="text-[11px] text-muted">
              Order runs on the account selected below.
            </p>
          </div>
          <label className="block text-[10px] uppercase tracking-wide text-muted">
            Account
            <select
              required
              value={form.accountNumber}
              onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
              className="mt-1 w-full text-xs px-2 py-2 rounded-md border border-hairline bg-canvas text-ink"
            >
              {!form.accountNumber && <option value="">Select account…</option>}
              {accounts.map((a) => (
                <option key={a.accountNumber} value={a.accountNumber}>
                  {accountLabel ? accountLabel(a) : maskAccount(a.accountNumber)}
                  {a.accountType ? ` · ${a.accountType}` : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
            <label className="text-[10px] uppercase tracking-wide text-muted col-span-1">
              Symbol
              <input
                required
                value={form.symbol}
                onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink"
              />
            </label>
            <label className="text-[10px] uppercase tracking-wide text-muted col-span-1">
              Side
              <select
                value={form.instruction}
                onChange={(e) => setForm({ ...form, instruction: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink"
              >
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
                <option value="BUY_TO_COVER">BUY_TO_COVER</option>
                <option value="SELL_SHORT">SELL_SHORT</option>
                <option value="BUY_TO_OPEN">BUY_TO_OPEN</option>
                <option value="BUY_TO_CLOSE">BUY_TO_CLOSE</option>
                <option value="SELL_TO_OPEN">SELL_TO_OPEN</option>
                <option value="SELL_TO_CLOSE">SELL_TO_CLOSE</option>
              </select>
            </label>
            <label className="text-[10px] uppercase tracking-wide text-muted">
              Qty
              <input
                required
                type="number"
                min="0"
                step="any"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink num"
              />
            </label>
            <label className="text-[10px] uppercase tracking-wide text-muted">
              Type
              <select
                value={form.orderType}
                onChange={(e) => setForm({ ...form, orderType: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink"
              >
                <option value="MARKET">MARKET</option>
                <option value="LIMIT">LIMIT</option>
                <option value="STOP">STOP</option>
                <option value="STOP_LIMIT">STOP_LIMIT</option>
                <option value="TRAILING_STOP">TRAILING_STOP</option>
              </select>
            </label>
            <label className="text-[10px] uppercase tracking-wide text-muted">
              Limit
              <input
                type="number"
                step="any"
                disabled={!['LIMIT', 'STOP_LIMIT'].includes(form.orderType)}
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink num disabled:opacity-40"
              />
            </label>
            <label className="text-[10px] uppercase tracking-wide text-muted">
              Stop / trail
              <input
                type="number"
                step="any"
                disabled={!['STOP', 'STOP_LIMIT', 'TRAILING_STOP'].includes(form.orderType)}
                value={form.stopPrice}
                onChange={(e) => setForm({ ...form, stopPrice: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink num disabled:opacity-40"
              />
            </label>
            <label className="text-[10px] uppercase tracking-wide text-muted">
              Asset
              <select
                value={form.assetType}
                onChange={(e) => setForm({ ...form, assetType: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink"
              >
                <option value="EQUITY">EQUITY</option>
                <option value="MUTUAL_FUND">MUTUAL_FUND</option>
                <option value="OPTION">OPTION</option>
                <option value="ETF">ETF</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <label className="text-[10px] uppercase tracking-wide text-muted">
              Duration
              <select
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink"
              >
                <option value="DAY">DAY</option>
                <option value="GOOD_TILL_CANCEL">GTC</option>
                <option value="FILL_OR_KILL">FOK</option>
                <option value="IMMEDIATE_OR_CANCEL">IOC</option>
              </select>
            </label>
            <label className="text-[10px] uppercase tracking-wide text-muted">
              Session
              <select
                value={form.session}
                onChange={(e) => setForm({ ...form, session: e.target.value })}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink"
              >
                <option value="NORMAL">NORMAL</option>
                <option value="AM">AM</option>
                <option value="PM">PM</option>
                <option value="SEAMLESS">SEAMLESS</option>
              </select>
            </label>
            <div className="flex items-end col-span-2">
              <button
                type="submit"
                disabled={placing || !form.accountNumber}
                className="w-full text-xs px-3 py-1.5 rounded-md bg-action text-white hover:opacity-90 disabled:opacity-50"
              >
                {placing ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  `Submit on ${
                    form.accountNumber
                      ? maskAccount(form.accountNumber)
                      : '…'
                  }`
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="lt-card overflow-hidden">
        {loading && orders.length === 0 ? (
          <div className="py-10 text-center">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted" />
          </div>
        ) : orders.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted">No orders in this window.</div>
        ) : (
          <>
            {/* Mobile order cards */}
            <div className="md:hidden divide-y divide-hairline">
              {orders.map((o) => (
                <div key={`${o.accountNumber}-${o.orderId}`} className="px-3 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-ink">{o.symbol || '—'}</div>
                      <div className="text-[10px] text-muted mt-0.5">
                        {o.instruction || '—'} · {o.orderType}
                        {viewMode === 'all' ? ` · ${maskAccount(o.accountNumber)}` : ''}
                      </div>
                    </div>
                    <div className={`text-xs font-medium shrink-0 ${statusTone(o.status)}`}>
                      {o.status}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <div>
                      <div className="text-muted uppercase tracking-wide text-[9px]">Qty</div>
                      <div className="num text-ink">{o.quantity ?? '—'}</div>
                    </div>
                    <div>
                      <div className="text-muted uppercase tracking-wide text-[9px]">Filled</div>
                      <div className="num text-ink">{o.filledQuantity ?? '—'}</div>
                    </div>
                    <div>
                      <div className="text-muted uppercase tracking-wide text-[9px]">Price</div>
                      <div className="num text-ink">
                        {o.price != null
                          ? money(o.price)
                          : o.stopPrice != null
                            ? `stop ${money(o.stopPrice)}`
                            : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[10px] text-muted">
                    <span className="num">{formatTime(o.enteredTime)}</span>
                    {o.cancelable ? (
                      <button
                        type="button"
                        onClick={() => handleCancel(o)}
                        disabled={cancelingId === o.orderId}
                        className="inline-flex items-center gap-1 text-[11px] text-bear hover:underline disabled:opacity-50"
                      >
                        <FontAwesomeIcon
                          icon={cancelingId === o.orderId ? faSpinner : faTimes}
                          className={cancelingId === o.orderId ? 'animate-spin' : ''}
                        />
                        Cancel
                      </button>
                    ) : (
                      <span className="num">#{o.orderId}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[1000px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted border-b border-hairline">
                    <th className="px-3 py-2">Entered</th>
                    <th className="px-3 py-2">Symbol</th>
                    <th className="px-3 py-2">Side</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Qty</th>
                    <th className="px-3 py-2">Filled</th>
                    <th className="px-3 py-2">Price</th>
                    <th className="px-3 py-2">Status</th>
                    {viewMode === 'all' && <th className="px-3 py-2">Account</th>}
                    <th className="px-3 py-2">Id</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr
                      key={`${o.accountNumber}-${o.orderId}`}
                      className="border-b border-hairline last:border-0 hover:bg-elevated/50"
                    >
                      <td className="px-3 py-2 text-xs num text-muted whitespace-nowrap">
                        {formatTime(o.enteredTime)}
                      </td>
                      <td className="px-3 py-2 font-medium text-ink">
                        {o.symbol || '—'}
                        <div className="text-[10px] text-muted">{o.assetType}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{o.instruction || '—'}</td>
                      <td className="px-3 py-2 text-xs">{o.orderType}</td>
                      <td className="px-3 py-2 num">{o.quantity ?? '—'}</td>
                      <td className="px-3 py-2 num">{o.filledQuantity ?? '—'}</td>
                      <td className="px-3 py-2 num whitespace-nowrap">
                        {o.price != null ? money(o.price) : o.stopPrice != null ? `stop ${money(o.stopPrice)}` : '—'}
                      </td>
                      <td className={`px-3 py-2 text-xs font-medium ${statusTone(o.status)}`}>
                        {o.status}
                      </td>
                      {viewMode === 'all' && (
                        <td className="px-3 py-2 text-xs num text-muted">
                          {maskAccount(o.accountNumber)}
                        </td>
                      )}
                      <td className="px-3 py-2 text-xs num text-muted">{o.orderId}</td>
                      <td className="px-3 py-2 text-right">
                        {o.cancelable && (
                          <button
                            type="button"
                            onClick={() => handleCancel(o)}
                            disabled={cancelingId === o.orderId}
                            className="inline-flex items-center gap-1 text-[11px] text-bear hover:underline disabled:opacity-50"
                            title="Cancel order"
                          >
                            <FontAwesomeIcon
                              icon={cancelingId === o.orderId ? faSpinner : faTimes}
                              className={cancelingId === o.orderId ? 'animate-spin' : ''}
                            />
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
